import Router, { useRouter } from 'next/router';
import { memo, useEffect, useRef, useState } from 'react';

import { useTrackAgentPanelOpened } from '@air/analytics';

import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import {
  clearWorkspaceAgentOnboardingQueryParams,
  getWorkspaceAgentOnboardingRoleHandoff,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentOnboardingRoleHandoff';
import {
  isWorkspaceAgentPanelExpanded,
  useWorkspaceAgentPanelStore,
} from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { subscribePushedRouterHref } from '~/utils/subscribePushedRouterHref';

/**
 * `onboardingRole` is how the intro's send lands in the workspace agent. The overlay and the panel
 * do not share a tree, and `chatId` / `agentUseCase` already open this panel from the URL, so the
 * role rides the same channel for that one navigation. Once this has read it, the params come off
 * the URL and the answers live in memory until a real session replaces them.
 */
export const WorkspaceAgentOnboardingRoleUrlSync = memo(() => {
  const router = useRouter();
  const isRouterReady = router.isReady;
  const { resetSession } = usePrivateWorkspaceAgentPanelSession();
  const dock = useWorkspaceAgentPanelStore((state) => state.dock);
  const showChat = useWorkspaceAgentPanelStore((state) => state.showChat);
  const setOnboardingHandoff = useWorkspaceAgentPanelStore((state) => state.setOnboardingHandoff);
  const { trackAgentPanelOpened } = useTrackAgentPanelOpened();
  const [hasPanelStoreHydrated, setHasPanelStoreHydrated] = useState(() =>
    useWorkspaceAgentPanelStore.persist.hasHydrated(),
  );
  const [locationSearch, setLocationSearch] = useState(() =>
    typeof window === 'undefined' ? '' : window.location.search,
  );
  const appliedHandoffSearchRef = useRef<string | null>(null);

  useEffect(() => {
    if (useWorkspaceAgentPanelStore.persist.hasHydrated()) {
      setHasPanelStoreHydrated(true);
      return;
    }

    return useWorkspaceAgentPanelStore.persist.onFinishHydration(() => {
      setHasPanelStoreHydrated(true);
    });
  }, []);

  useEffect(() => {
    const syncSearchFromHref = (href: string) => {
      setLocationSearch(new URL(href, window.location.origin).search);
    };

    const syncSearchFromWindow = () => {
      syncSearchFromHref(window.location.href);
    };

    syncSearchFromWindow();

    const unsubscribePushedHref = subscribePushedRouterHref(syncSearchFromHref);
    Router.events.on('routeChangeComplete', syncSearchFromHref);
    window.addEventListener('popstate', syncSearchFromWindow);

    return () => {
      unsubscribePushedHref();
      Router.events.off('routeChangeComplete', syncSearchFromHref);
      window.removeEventListener('popstate', syncSearchFromWindow);
    };
  }, []);

  useEffect(() => {
    if (!isRouterReady || !hasPanelStoreHydrated) {
      return;
    }

    const windowSearch = typeof window === 'undefined' ? '' : window.location.search;
    const search = getWorkspaceAgentOnboardingRoleHandoff(locationSearch) ? locationSearch : windowSearch;
    const handoff = getWorkspaceAgentOnboardingRoleHandoff(search);

    if (!handoff) {
      appliedHandoffSearchRef.current = null;
      return;
    }

    // A real chat already on the URL stays. Only the intro answers come off.
    if (!handoff.shouldOpenFreshThread) {
      clearWorkspaceAgentOnboardingQueryParams();
      return;
    }

    if (appliedHandoffSearchRef.current === search) {
      return;
    }

    appliedHandoffSearchRef.current = search;
    setOnboardingHandoff({ role: handoff.role, timeSinkIds: handoff.timeSinkIds });

    const wasMinimized = !isWorkspaceAgentPanelExpanded({ dock });

    resetSession();
    showChat({ focusComposer: false });
    clearWorkspaceAgentOnboardingQueryParams();

    if (wasMinimized) {
      trackAgentPanelOpened({ location: 'url' });
    }
  }, [
    dock,
    hasPanelStoreHydrated,
    isRouterReady,
    locationSearch,
    resetSession,
    setOnboardingHandoff,
    showChat,
    trackAgentPanelOpened,
  ]);

  return null;
});

WorkspaceAgentOnboardingRoleUrlSync.displayName = 'WorkspaceAgentOnboardingRoleUrlSync';
